import React, { useState, useMemo } from "react";
import { cn } from "../utils/cn";
import { formatCurrency } from "../utils/formatting";

interface IncomeAnalysisProps {
  /** CPP annual income */
  cppAnnual: number;
  /** OAS annual income */
  oasAnnual: number;
  /** Investment income annual */
  investmentIncomeAnnual: number;
  /** Pension annual income (optional) */
  pensionAnnual?: number;
  /** Monthly expenses in retirement */
  monthlyExpenses: number;
  /** Year retirement begins */
  retirementYear: number;
  /** Age at retirement */
  retirementAge: number;
  /** Currency code */
  currency?: string;
  /** Called when user clicks shortfall CTA */
  onAdjustExpenses?: () => void;
}

type ViewMode = "annual" | "monthly";

interface IncomeSource {
  label: string;
  annualAmount: number;
  colorClass: string;
}

/**
 * SVG donut chart for income composition.
 * Uses stroke-dasharray segments to create a ring chart.
 */
function IncomeDonut({
  sources,
  totalAnnual,
  currency,
}: {
  sources: IncomeSource[];
  totalAnnual: number;
  currency: string;
}) {
  const radius = 38;
  const circumference = 2 * Math.PI * radius;

  // Build cumulative offsets for each segment
  const activeSources = sources.filter((s) => s.annualAmount > 0);
  const segments = activeSources.reduce<
    Array<IncomeSource & { dashLength: number; gap: number; offset: number }>
  >((acc, source) => {
    const fraction = totalAnnual > 0 ? source.annualAmount / totalAnnual : 0;
    const dashLength = fraction * circumference;
    const gap = circumference - dashLength;
    const prevOffset = acc.length > 0 ? acc[acc.length - 1] : undefined;
    const cumulativeLength = prevOffset
      ? -prevOffset.offset + prevOffset.dashLength
      : 0;
    acc.push({ ...source, dashLength, gap, offset: -cumulativeLength });
    return acc;
  }, []);

  const strokeColors: Record<string, string> = {
    "income-source-dot--cpp": "#3b82f6",
    "income-source-dot--oas": "#22c55e",
    "income-source-dot--investment": "#a78bfa",
    "income-source-dot--pension": "#14b8a6",
  };

  return (
    <div className="income-donut">
      <svg viewBox="0 0 100 100" width="96" height="96">
        {/* Background ring */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="10"
        />
        {/* Segments */}
        {segments.map((seg) => (
          <circle
            key={seg.label}
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={strokeColors[seg.colorClass] ?? "#94a3b8"}
            strokeWidth="10"
            strokeDasharray={`${seg.dashLength} ${seg.gap}`}
            strokeDashoffset={seg.offset}
            transform="rotate(-90 50 50)"
          />
        ))}
      </svg>
      <span className="income-donut-center-text">
        {formatCurrency(totalAnnual, currency)}
      </span>
    </div>
  );
}

export const IncomeAnalysis: React.FC<IncomeAnalysisProps> = React.memo(
  ({
    cppAnnual,
    oasAnnual,
    investmentIncomeAnnual,
    pensionAnnual = 0,
    monthlyExpenses,
    retirementYear,
    retirementAge,
    currency = "CAD",
    onAdjustExpenses,
  }) => {
    const [viewMode, setViewMode] = useState<ViewMode>("annual");

    const sources: IncomeSource[] = useMemo(
      () => [
        {
          label: "CPP at 65",
          annualAmount: cppAnnual,
          colorClass: "income-source-dot--cpp",
        },
        {
          label: "OAS at 65",
          annualAmount: oasAnnual,
          colorClass: "income-source-dot--oas",
        },
        {
          label: "Investment Income",
          annualAmount: investmentIncomeAnnual,
          colorClass: "income-source-dot--investment",
        },
        ...(pensionAnnual > 0
          ? [
              {
                label: "Pension",
                annualAmount: pensionAnnual,
                colorClass: "income-source-dot--pension",
              },
            ]
          : []),
      ],
      [cppAnnual, oasAnnual, investmentIncomeAnnual, pensionAnnual]
    );

    const totalAnnualIncome = useMemo(
      () => sources.reduce((sum, s) => sum + s.annualAmount, 0),
      [sources]
    );

    const totalMonthlyIncome = Math.round(totalAnnualIncome / 12);
    const annualExpenses = monthlyExpenses * 12;

    const annualShortfall = totalAnnualIncome - annualExpenses;
    const monthlyShortfall = totalMonthlyIncome - monthlyExpenses;

    const isShortfall = annualShortfall < 0;

    const formatValue = (annual: number): string => {
      if (viewMode === "monthly") {
        return `${formatCurrency(Math.round(annual / 12), currency)}/mo`;
      }
      return `${formatCurrency(annual, currency)}/yr`;
    };

    return (
      <div className="income-analysis">
        <div className="income-analysis-header">
          <h3 className="income-analysis-title">Retirement Income Analysis</h3>
          <span className="income-analysis-subtitle">
            First year of retirement ({retirementYear}, age {retirementAge})
          </span>
        </div>

        {/* Toggle */}
        <div className="income-toggle">
          <button
            type="button"
            className={cn(
              "income-toggle-btn",
              viewMode === "annual" && "income-toggle-btn--active"
            )}
            onClick={() => setViewMode("annual")}
          >
            Annual
          </button>
          <button
            type="button"
            className={cn(
              "income-toggle-btn",
              viewMode === "monthly" && "income-toggle-btn--active"
            )}
            onClick={() => setViewMode("monthly")}
          >
            Monthly
          </button>
        </div>

        {/* Income sources with donut */}
        <div className="income-donut-container">
          <IncomeDonut
            sources={sources}
            totalAnnual={totalAnnualIncome}
            currency={currency}
          />
          <div className="income-source-list">
            {sources.map((source) => (
              <div key={source.label} className="income-source-row">
                <span className="income-source-label">
                  <span
                    className={cn("income-source-dot", source.colorClass)}
                  />
                  {source.label}
                </span>
                <span className="income-source-value">
                  {formatValue(source.annualAmount)}
                </span>
              </div>
            ))}
            <div className="income-source-row income-source-total">
              <span className="income-source-label">Total</span>
              <span className="income-source-value">
                {formatValue(totalAnnualIncome)}
              </span>
            </div>
          </div>
        </div>

        {/* Shortfall / Surplus card */}
        {isShortfall ? (
          <div className="income-shortfall-card">
            <div className="income-shortfall-header">
              <span className="income-shortfall-icon" aria-hidden="true">
                ⚠️
              </span>
              <span className="income-shortfall-title">
                {viewMode === "monthly" ? "Monthly" : "Annual"} Shortfall
              </span>
            </div>
            <div className="income-shortfall-detail">
              <div className="income-shortfall-row">
                <span>
                  {viewMode === "monthly" ? "Monthly" : "Annual"} Income
                </span>
                <span>
                  {viewMode === "monthly"
                    ? formatCurrency(totalMonthlyIncome, currency)
                    : formatCurrency(totalAnnualIncome, currency)}
                </span>
              </div>
              <div className="income-shortfall-row">
                <span>
                  {viewMode === "monthly" ? "Monthly" : "Annual"} Expenses
                </span>
                <span>
                  {viewMode === "monthly"
                    ? formatCurrency(monthlyExpenses, currency)
                    : formatCurrency(annualExpenses, currency)}
                </span>
              </div>
              <div className="income-shortfall-row income-shortfall-row--total">
                <span>Shortfall</span>
                <span className="income-shortfall-amount">
                  {viewMode === "monthly"
                    ? formatCurrency(monthlyShortfall, currency)
                    : formatCurrency(annualShortfall, currency)}
                </span>
              </div>
            </div>
            <p className="income-shortfall-explanation">
              This gap will be covered by portfolio withdrawals from your
              investment accounts.
            </p>
            {onAdjustExpenses && (
              <button
                type="button"
                className="income-shortfall-cta"
                onClick={onAdjustExpenses}
              >
                Reduce the gap → Adjust expenses
              </button>
            )}
          </div>
        ) : (
          <div className="income-surplus-card">
            <div className="income-surplus-header">
              <span className="income-surplus-icon" aria-hidden="true">
                ✅
              </span>
              <span className="income-surplus-title">
                {viewMode === "monthly" ? "Monthly" : "Annual"} Surplus
              </span>
            </div>
            <div className="income-surplus-amount">
              +
              {viewMode === "monthly"
                ? formatCurrency(Math.abs(monthlyShortfall), currency)
                : formatCurrency(Math.abs(annualShortfall), currency)}
            </div>
            <p className="income-surplus-explanation">
              Your retirement income covers your expenses with room to spare.
            </p>
          </div>
        )}
      </div>
    );
  }
);

IncomeAnalysis.displayName = "IncomeAnalysis";
